import { useEffect, useMemo } from 'react';
import { useConfirmation } from '../hooks/useConfirmation';
import { trackEvent } from '../utils/track';
import type { BookingConfirmation, BookingStatus } from '../types';
import {
  IconAlert,
  IconCheckCircle,
  IconClock,
  IconLoader,
  IconRotate,
  IconXCircle,
} from '../components/icons';

function readBookingId(): string | null {
  const url = new URL(window.location.href);
  return url.searchParams.get('booking');
}

function formatCurrency(value: number, currency: string) {
  const code = currency === 'R$' ? 'BRL' : currency;
  try {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: code }).format(value);
  } catch {
    return `${currency} ${value.toFixed(2)}`;
  }
}

function formatDateTime(iso: string) {
  const d = new Date(iso);
  const date = d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });
  const time = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  return `${date} às ${time}`;
}

// Cabecalho compartilhado entre todos os status — icone circular grande
// (verde/azul/ambar conforme variant) + titulo display + lead curto.
// Mesma cascata visual do .confirm-screen (BookingFlow) → continuidade total.
function ConfirmHero({
  variant,
  icon,
  title,
  lead,
}: {
  variant: 'paid' | 'pending' | 'failed' | 'timeout';
  icon: React.ReactNode;
  title: string;
  lead: string;
}) {
  return (
    <header className={`confirm__hero confirm__hero--${variant}`}>
      <span className="confirm__icon-wrap" aria-hidden="true">
        {icon}
      </span>
      <h1 className="confirm__title">{title}</h1>
      <p className="confirm__lead">{lead}</p>
    </header>
  );
}

function StatusPending() {
  return (
    <section className="confirm confirm--pending">
      <ConfirmHero
        variant="pending"
        icon={<IconLoader size={30} />}
        title="Confirmando seu pagamento"
        lead="Aguarde alguns segundos. Estamos validando com a operadora."
      />
      <div className="confirm__card confirm__card--skeleton">
        <div className="skeleton skeleton--line" />
        <div className="skeleton skeleton--line skeleton--line-sm" />
      </div>
    </section>
  );
}

function StatusPaid({ data }: { data: BookingConfirmation }) {
  return (
    <section className="confirm confirm--paid">
      <ConfirmHero
        variant="paid"
        icon={<IconCheckCircle size={30} />}
        title="Reserva confirmada"
        lead="Sua vaga está garantida. Enviamos os detalhes por e-mail e WhatsApp."
      />

      {data.bookingCode && (
        <div className="confirm__code">
          <span>Código da reserva</span>
          <strong>{data.bookingCode}</strong>
        </div>
      )}

      <dl className="confirm__card confirm__list">
        <div>
          <dt>Experiência</dt>
          <dd>{data.experienceName}</dd>
        </div>
        <div>
          <dt>Data</dt>
          <dd>{formatDateTime(data.eventDateTime)}</dd>
        </div>
        <div>
          <dt>Pessoas</dt>
          <dd>{data.partySize}</dd>
        </div>
        <div>
          <dt>Valor pago</dt>
          <dd>{formatCurrency(data.amount, data.currency)}</dd>
        </div>
        <div>
          <dt>Titular</dt>
          <dd>{data.customerName}</dd>
        </div>
        <div>
          <dt>E-mail</dt>
          <dd>{data.customerEmail}</dd>
        </div>
      </dl>

      {data.bookingInstructions && (
        <section className="confirm__instructions">
          <h3>Instruções</h3>
          <p>{data.bookingInstructions}</p>
        </section>
      )}

      <div className="confirm__cta-row">
        <a href="/" className="btn btn--brand btn--lg">Voltar para a home</a>
      </div>
    </section>
  );
}

function StatusFailed({ status }: { status: BookingStatus }) {
  const Icon =
    status === 'capacity_conflict' ? IconAlert : status === 'refunded' ? IconRotate : IconXCircle;
  const title =
    status === 'capacity_conflict'
      ? 'Vaga indisponível'
      : status === 'refunded'
        ? 'Pagamento estornado'
        : 'Pagamento não confirmado';
  const lead =
    status === 'capacity_conflict'
      ? 'A vaga foi preenchida por outro cliente. Seu pagamento será estornado automaticamente.'
      : status === 'refunded'
        ? 'Seu pagamento foi estornado. Caso tenha dúvidas, fale com nosso atendimento.'
        : 'Não conseguimos confirmar o pagamento. Você pode tentar novamente.';

  return (
    <section className="confirm confirm--failed">
      <ConfirmHero
        variant="failed"
        icon={<Icon size={30} />}
        title={title}
        lead={lead}
      />
      <div className="confirm__cta-row">
        <a href="/" className="btn btn--brand btn--lg">Voltar para a home</a>
      </div>
    </section>
  );
}

function StatusTimeout() {
  return (
    <section className="confirm confirm--timeout">
      <ConfirmHero
        variant="timeout"
        icon={<IconClock size={30} />}
        title="Ainda aguardando"
        lead="O pagamento pode levar alguns minutos para ser confirmado. Enviaremos a confirmação por e-mail assim que finalizado."
      />
      <div className="confirm__cta-row">
        <button
          type="button"
          className="btn btn--brand btn--lg"
          onClick={() => window.location.reload()}
        >
          Atualizar
        </button>
      </div>
    </section>
  );
}

export function ConfirmationPage() {
  const bookingId = useMemo(readBookingId, []);
  const { data, error, loading, timedOut } = useConfirmation(bookingId);

  // Funnel: tela final pos-pagamento Instant. Reportar so quando temos dados
  // (status terminal). Status 'paid' eh o sucesso, mas mandamos o status real
  // pra distinguir paid/refunded/failed/etc na analise.
  useEffect(() => {
    if (!data) return;
    trackEvent('booking_confirmation_view', {
      bookingType: 'Confirmação Imediata',
      status: data.status,
      bookingCode: data.bookingCode ?? undefined,
      partySize: data.partySize,
      eventTime: data.eventDateTime,
      surface: 'instant_postpayment',
    });
  }, [data]);

  if (error) {
    return (
      <main className="confirm-page">
        <section className="confirm confirm--failed">
          <ConfirmHero
            variant="failed"
            icon={<IconXCircle size={30} />}
            title="Não conseguimos carregar sua reserva"
            lead={error.message}
          />
          <div className="confirm__cta-row">
            <a href="/" className="btn btn--brand btn--lg">Voltar para a home</a>
          </div>
        </section>
      </main>
    );
  }

  if (loading || !data) {
    return (
      <main className="confirm-page">
        <StatusPending />
      </main>
    );
  }

  if (data.status === 'pending') {
    return (
      <main className="confirm-page">
        {timedOut ? <StatusTimeout /> : <StatusPending />}
      </main>
    );
  }

  if (data.status === 'paid') {
    return (
      <main className="confirm-page">
        <StatusPaid data={data} />
      </main>
    );
  }

  return (
    <main className="confirm-page">
      <StatusFailed status={data.status} />
    </main>
  );
}
